import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import GroupsIcon from "@mui/icons-material/Groups";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useGetEntityMemberPageQuery } from "_shared/entity-members/_api/entity-member-api";
import EntityMemberQuery from "_shared/entity-members/_api/queries/entity-member-query";
import { useGetActiveUserEntityMemberInvitationsQuery } from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import { useState } from "react";
import { Link as RouterLink } from "react-router";

/**
 * A page of one member, read only for the total the server sends beside it: the banner asks how many
 * people the organization has, not who they are (#5442).
 */
export const MEMBER_COUNT_QUERY = new EntityMemberQuery({
  paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 1 }),
});

/**
 * A page of one active invitation, read for the same reason: the banner asks whether anybody is
 * invited and waiting, which is the total, not the list (#5444).
 */
export const INVITATION_COUNT_QUERY = { pageIndex: 0, pageSize: 1 } as const;

/**
 * Where a leader's "not now" is kept. Per browser, like the dashboard's own arrangement: this is a
 * reminder, not a record, and nothing about it is worth a round trip.
 */
export const buildYourTeamDismissalStorageKey = "attorneyhub.dashboard.build-your-team-dismissed";

export function readBuildYourTeamDismissal(storage: Pick<Storage, "getItem"> | undefined): boolean {
  try {
    return storage?.getItem(buildYourTeamDismissalStorageKey) === "true";
  } catch {
    return false;
  }
}

export function writeBuildYourTeamDismissal(storage: Pick<Storage, "setItem"> | undefined): void {
  try {
    storage?.setItem(buildYourTeamDismissalStorageKey, "true");
  } catch {
    // A reader who cannot save their choice still gets to make it for this visit.
  }
}

/** Brings the banner back after "Not now", from Settings' "Show dismissed tips again". */
export function forgetBuildYourTeamDismissal(storage: Pick<Storage, "removeItem"> | undefined): void {
  try {
    storage?.removeItem(buildYourTeamDismissalStorageKey);
  } catch {
    // Storage the browser refuses held no dismissal to forget.
  }
}

/**
 * The dashboard's standing ask for a firm's roster of colleagues.
 *
 * The notification raised when a firm's Attorney Hub proposal is hired is the prompt; this is what
 * makes the prompt survive being ignored. A notification scrolls away and a leader who never opened
 * the bell never saw it at all, while form letter 1 has already told this firm that getting its
 * people onto the platform is the leader's job.
 *
 * It asks two questions rather than looking the prompt up. Whether a notification is still
 * outstanding is not something the browser can ask — the notifications list has no topic-category
 * filter — but whether the roster has been entered is, and that is the thing the banner is actually
 * about. So: somebody who may invite, an organization that is still only them, and nobody invited
 * and waiting. All three stop being true the moment the roster is entered, invitation by invitation
 * or a spreadsheet at a time.
 *
 * The cost of reading it that way is that a sole practitioner who administers their own
 * organization is asked too — which is why "Not now" exists, and why it is remembered.
 */
export default function BuildYourTeamBanner() {
  const user = useSessionUser();
  const narrow = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [dismissed, setDismissed] = useState(() =>
    readBuildYourTeamDismissal(globalThis.localStorage),
  );

  // The same capability the page behind "Add colleagues" asks for, so the banner never offers a
  // roster its reader would be refused.
  const mayInvite = user?.canInviteColleagues() === true;
  // Inviting implies reading the pending invitations, but not reading the members: that endpoint
  // asserts Member Information, which an inviter need not hold. Such a reader would be answered 403,
  // with a console error, on a dashboard they are perfectly entitled to, so the banner stays silent
  // for them and makes neither read, since it could not draw on one alone.
  const mayReadMembers = user?.sessionPermissions?.includesReadAccessToMemberInformation === true;
  const legalEntityId = user?.legalEntityId;
  const skip = !mayInvite || !mayReadMembers || !legalEntityId || dismissed;

  const membersQuery = useGetEntityMemberPageQuery(
    { legalEntityId: legalEntityId?.value ?? "", query: MEMBER_COUNT_QUERY },
    { skip },
  );
  const invitationsQuery = useGetActiveUserEntityMemberInvitationsQuery(
    // Non-null asserted rather than defaulted: the skip already covers the absent case, and a
    // stand-in id would ask the API about an organization nobody has.
    { entityId: legalEntityId!, ...INVITATION_COUNT_QUERY },
    { skip },
  );

  if (skip) return null;
  // Silent while either answer is unknown, and silent if either could not be read. A banner that
  // appeared and then vanished would be read as a mistake, and one shown on a failed read would
  // tell a firm of forty to go and add its people.
  if (membersQuery.data === undefined || invitationsQuery.data === undefined) return null;

  const rosterIsEmpty = membersQuery.data.headers.totalElements <= 1 && invitationsQuery.data.headers.totalElements === 0;
  if (!rosterIsEmpty) return null;

  const body =
    "Everyone at your firm who works on a client's matters needs their own account and a " +
    "profile listing their credentials. Until they have both, their time cannot be processed for " +
    "billing.";

  const putAway = () => {
    setDismissed(true);
    writeBuildYourTeamDismissal(globalThis.localStorage);
  };

  const addColleagues = (
    <Button
      component={RouterLink}
      size="small"
      to={`/invite-colleagues/${legalEntityId.value}`}
      variant="contained"
    >
      Add colleagues
    </Button>
  );
  const notNow = (
    <IconButton aria-label="Not now" onClick={putAway} size="small">
      <CloseRoundedIcon fontSize="small" />
    </IconButton>
  );

  // On a phone an Alert's icon column and its action column both come out of the message's width,
  // and what was left for the heading and the paragraph was about 150px: nine lines of body text
  // beside a button (#5907). So below `sm` the alert keeps only the close in its action column,
  // where it still sits at the top right; the icon moves into the heading so the paragraph can run
  // under it at the alert's full width; and the button follows the paragraph, which is also the
  // order it is read in.
  if (narrow) {
    return (
      <Alert icon={false} severity="info" action={notNow}>
        <AlertTitle sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
          <GroupsIcon color="info" sx={{ fontSize: 22, opacity: 0.9 }} />
          Add your colleagues to Attorney Hub
        </AlertTitle>
        <Typography variant="body2">{body}</Typography>
        <Stack direction="row" sx={{ marginTop: 1.5 }}>
          {addColleagues}
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert
      icon={<GroupsIcon fontSize="inherit" />}
      severity="info"
      action={
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {addColleagues}
          {notNow}
        </Stack>
      }
    >
      <AlertTitle>Add your colleagues to Attorney Hub</AlertTitle>
      <Typography variant="body2">{body}</Typography>
    </Alert>
  );
}
